import { useEffect, useMemo, useState } from 'react';
import websocketService from '../services/websocket';
import apiService, { ServiceType } from '../services/api';
import type { PriceResponseDto } from '../api-schema/generated/types';

const coreService = apiService.getService(ServiceType.CORE);

/** Strip the exchange prefix: "HSX:VNM" → "VNM", "HNXDS:VN30F2609" → "VN30F2609". */
export const bareSymbol = (symbol: string): string => {
    const idx = symbol.indexOf(':');
    return idx >= 0 ? symbol.slice(idx + 1) : symbol;
};

/**
 * Stream live prices for a *set* of symbols (stocks and/or futures), keyed by
 * bare symbol.
 *
 * `useWebSocketPriceData` handles a single symbol, which forces one hook per
 * instrument — impossible for a table whose rows the user adds and removes.
 * The websocket service already multiplexes rooms per symbol, so one effect can
 * hold every subscription and re-diff when the list changes.
 *
 * Each symbol is also seeded from `/price/latest/{symbol}` so a row shows a
 * price before its first tick (outside trading hours that is the only value
 * there will ever be).
 */
export const useLivePrices = (
    symbols: (string | null | undefined)[],
    enabled: boolean = true,
): Record<string, PriceResponseDto> => {
    // Sorted + deduped so the effect only re-runs when the SET changes, not on
    // every parent render or reorder.
    const key = useMemo(
        () =>
            Array.from(
                new Set(
                    symbols
                        .filter((s): s is string => !!s && !!s.trim())
                        .map((s) => bareSymbol(s.trim())),
                ),
            )
                .sort()
                .join(','),
        [symbols],
    );

    const [prices, setPrices] = useState<Record<string, PriceResponseDto>>({});

    useEffect(() => {
        if (!enabled || !key) {
            setPrices({});
            return;
        }

        const list = key.split(',');
        let cancelled = false;

        // Drop symbols that just left the list, keep the ones still streaming so
        // their rows don't blank out on every add/remove.
        setPrices((prev) => {
            const next: Record<string, PriceResponseDto> = {};
            for (const symbol of list) {
                if (prev[symbol]) next[symbol] = prev[symbol];
            }
            return next;
        });

        websocketService.connect();

        const unsubscribers = list.map((symbol) =>
            websocketService.subscribe(symbol, (data: PriceResponseDto) => {
                if (cancelled) return;
                setPrices((prev) => ({ ...prev, [symbol]: data }));
            }),
        );

        list.forEach(async (symbol) => {
            try {
                const response = await coreService.get(`/price/latest/${symbol}`, { timeout: 5000 });
                if (cancelled || !response.data) return;
                setPrices((prev) => {
                    // A websocket tick already landed — never overwrite it with
                    // the (older) REST snapshot.
                    if (prev[symbol]) return prev;
                    return { ...prev, [symbol]: response.data };
                });
            } catch {
                // No seed price for this symbol; the row renders "—" until a tick.
            }
        });

        return () => {
            cancelled = true;
            unsubscribers.forEach((unsubscribe) => unsubscribe());
        };
    }, [key, enabled]);

    return prices;
};

/** Latest matched price for `symbol`, or null when nothing has arrived yet. */
export const livePriceOf = (
    prices: Record<string, PriceResponseDto>,
    symbol: string | null | undefined,
): number | null => {
    if (!symbol) return null;
    const price = prices[bareSymbol(symbol)]?.latest_matched_price;
    return price != null && price > 0 ? price : null;
};
